import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Rocket, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import ConsentBoxes from "@/components/ConsentBoxes";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [consent, setConsent] = useState({ consent_account: false, consent_marketing: false });
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { name, email, password, whatsapp: whatsapp.trim() || null, ...consent });
      login(data.token, data.user, data.parent_unlock);
      toast.success("Account created! Let's add your first learner.");
      navigate("/parent");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#EAF7F4] font-parent flex items-center justify-center px-5 py-10">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8" data-testid="register-brand">
          <div className="w-10 h-10 rounded-xl bg-blue-600 border-2 border-slate-900 flex items-center justify-center shadow-[3px_3px_0_0_#0F172A]">
            <Rocket color="#fff" size={22} />
          </div>
          <span className="font-kids text-2xl font-bold text-slate-900">Anou Appran</span>
        </Link>
        <div className="nb-card bg-white p-7 sm:p-8">
          <h1 className="font-kids text-2xl font-bold text-slate-900">Create your account</h1>
          <p className="text-sm text-slate-500 mt-1">Parents sign up here to manage their kids.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="name">Your name</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required
                className="mt-1.5 h-11 rounded-xl border-2 border-slate-900" placeholder="Jane Doe" data-testid="register-name" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="mt-1.5 h-11 rounded-xl border-2 border-slate-900" placeholder="you@email.com" data-testid="register-email" />
            </div>
            <div>
              <Label htmlFor="whatsapp">WhatsApp number <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Input id="whatsapp" type="tel" inputMode="tel" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)}
                className="mt-1.5 h-11 rounded-xl border-2 border-slate-900" placeholder="5XXX XXXX (or +country code)" data-testid="register-whatsapp" />
              <p className="text-xs text-slate-500 mt-1">So we can help you quickly with payments and access.</p>
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6}
                className="mt-1.5 h-11 rounded-xl border-2 border-slate-900" placeholder="At least 6 characters" data-testid="register-password" />
            </div>
            <ConsentBoxes value={consent} onChange={setConsent} />
            <Button type="submit" disabled={loading}
              className="w-full h-11 rounded-full bg-blue-600 hover:bg-blue-700 border-2 border-slate-900 shadow-[4px_4px_0_0_#0F172A] font-semibold" data-testid="register-submit">
              {loading ? <Loader2 className="animate-spin" size={18} /> : "Create account"}
            </Button>
          </form>
          <p className="text-sm text-center text-slate-500 mt-5">
            Already have an account?{" "}
            <Link to="/login" className="font-semibold text-blue-600 hover:underline" data-testid="register-to-login">Log in</Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
